import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import LoadingSpinner from '../../LoadingSpinner';
import { useDelayedFlag } from '../../hooks/useDelayedFlag';
import ShowAlert from '../../ShowAlert';
import { commentService } from '../../clients/ApiClients';
import {
  CommentReturn,
  CommentVisibility,
} from '../../clients/rust-api-client';
import { billingErrorMessage } from '../../utils/billingHelpers';
import { useComments } from './useComments';
import { useAuthorNames } from './useAuthorNames';
import { buildCommentTree } from './commentTree';
import { threadCopy } from './commentLabels';
import {
  CommentSubject,
  CommentSubjectKindContext,
  subjectField,
} from './commentSubject';
import CommentComposer from './CommentComposer';
import CommentNodeView, { Avatar, commentAnchor } from './CommentNode';
import { ActivityEvent, byTime } from './activityEvents';
import { ActivityEventRow } from './ActivityEventRow';

const NO_EVENTS: ActivityEvent[] = [];

export interface CommentThreadProps {
  /** The violation or work order this thread is on — see commentSubject.ts. */
  subject: CommentSubject;
  /** The signed-in user's id, for deciding which comments are theirs. */
  currentUserId: string | null;
  canComment: boolean;
  /** Manager+. Gates the tab strip, the badges, and the internal composer. */
  canPostInternal: boolean;
  canModerate: boolean;
  canAttach: boolean;
  /**
   * The subject's own moments (created, notified, resolved, last edit)
   * to interleave with the comments. Only what the record stores — there
   * is no change history yet (#609); see activityEvents.ts.
   */
  events?: ActivityEvent[];
  /** The section heading. Defaults to "Activity". */
  title?: string;
  /**
   * A comment to point at — the reply a notification was about. Its thread
   * is shown, it is scrolled into view, and its border is outlined.
   */
  highlightCommentId?: string | null;
}

/**
 * A violation's or work order's Activity: its events and both comment
 * threads, interleaved in time order (Linear's layout, #603).
 */
export function CommentThread({
  subject,
  currentUserId,
  canComment,
  canPostInternal,
  canModerate,
  canAttach,
  events = NO_EVENTS,
  title = 'Activity',
  highlightCommentId = null,
}: CommentThreadProps) {
  const { comments, loading, error, clearError, setError, reload } =
    useComments(subject);
  const showSpinner = useDelayedFlag(loading);
  const authorName = useAuthorNames(comments);

  const [tab, setTab] = useState<CommentVisibility>(
    canPostInternal ? 'internal' : 'shared'
  );
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // One fetch, partitioned here: the list endpoint has no visibility
  // parameter, so a Manager receives both threads together and a
  // BoardMember receives only the shared one.
  const visible = useMemo(
    () => comments.filter((c) => c.visibility === tab),
    [comments, tab]
  );
  // Built per partition, never over both. One tree across both threads
  // would let a shared reply resolve an internal parent and disclose it.
  const tree = useMemo(() => buildCommentTree(visible), [visible]);

  const copy = threadCopy(tab, subject.kind);

  /**
   * Run a write, surface its error, and **rethrow**.
   *
   * Rethrowing rather than swallowing is what lets the composer tell "the
   * post failed" apart from "the post succeeded and had nothing to
   * attach". Swallowing conflated the two, and the composer treated a
   * failed post as done — clearing the text the author had just written.
   */
  const run = async <T,>(action: () => Promise<T>): Promise<T> => {
    setBusy(true);
    clearError();
    try {
      const result = await action();
      reload();
      return result;
    } catch (err) {
      console.error('Comment action failed:', err);
      setError(billingErrorMessage(err, 'That comment could not be saved.'));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const post =
    (visibility: CommentVisibility, parentId?: string) =>
    async (content: string): Promise<string | void> => {
      const created = await run(() =>
        commentService.createComment({
          commentCreate: {
            content,
            ...subjectField(subject),
            // Always sent, both values, every time. Omitting it means
            // `internal` on the server, so a shared reply that left it
            // out would never reach the audience it was written for.
            visibility,
            ...(parentId ? { parent_id: parentId } : {}),
          },
        })
      );
      // The id goes back to the composer so it can attach files to this
      // comment, and retry only that half if the attach fails.
      return created.data.id;
    };

  const handleReply = async (parent: CommentReturn, content: string) =>
    post(parent.visibility, parent.id)(content);

  const handleEdit = async (comment: CommentReturn, content: string) => {
    await run(() =>
      commentService.updateComment({
        id: comment.id,
        // No visibility field exists on the update payload: a thread is
        // immutable once posted. Demoting a shared comment would be a lie,
        // since it has already been read.
        commentUpdate: { content },
      })
    );
    cancel();
    return undefined;
  };

  const handleDelete = async (comment: CommentReturn) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await run(() => commentService.deleteComment({ id: comment.id }));
      cancel();
    } catch {
      // `run` has already put the message on screen.
    }
  };

  const startReply = (commentId: string) => {
    setEditingId(null);
    setReplyingToId(commentId);
  };

  const startEdit = (commentId: string) => {
    setReplyingToId(null);
    setEditingId(commentId);
  };

  const cancel = () => {
    setReplyingToId(null);
    setEditingId(null);
  };

  // Switching threads discards whatever was being typed, because the
  // composers are keyed on the tab and unmount with it. A composer that
  // survived the switch is exactly how an internal note ends up in the
  // shared thread.
  const selectTab = (next: CommentVisibility) => {
    cancel();
    setTab(next);
  };

  // The comment a notification pointed at: switch to its thread once, then
  // bring it into view. Once only, so the manager can switch away again.
  const highlighted = useMemo(
    () =>
      highlightCommentId
        ? (comments.find((c) => c.id === highlightCommentId) ?? null)
        : null,
    [comments, highlightCommentId]
  );
  const shownFor = useRef<string | null>(null);
  useEffect(() => {
    if (!highlighted || shownFor.current === highlighted.id) return;
    if (canPostInternal && tab !== highlighted.visibility) {
      // As `selectTab` does. Runs again once the other thread has rendered.
      setReplyingToId(null);
      setEditingId(null);
      setTab(highlighted.visibility);
      return;
    }
    shownFor.current = highlighted.id;
    document
      .getElementById(commentAnchor(highlighted.id))
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlighted, tab, canPostInternal]);

  // Events are the subject's, not a thread's: both audiences' timelines
  // show when it was created, noticed and resolved.
  const eventActors = useMemo(
    () =>
      events
        .filter((e) => e.actorId)
        .map((e) => ({ author_id: e.actorId!, author_realm: 'user' as const })),
    [events]
  );
  const actorName = useAuthorNames(eventActors);

  const timeline = useMemo(
    () =>
      byTime([
        ...events.map((event) => ({
          kind: 'event' as const,
          at: event.at,
          event,
        })),
        ...tree.map((node) => ({
          kind: 'comment' as const,
          at: node.comment.created_at,
          node,
        })),
      ]),
    [events, tree]
  );

  const tabButton = (value: CommentVisibility) => (
    <button
      key={value}
      type="button"
      onClick={() => selectTab(value)}
      aria-pressed={tab === value}
      className={clsx(
        'rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors',
        tab === value
          ? value === 'internal'
            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
            : 'bg-fg/10 text-fg'
          : 'text-fg-muted hover:text-fg'
      )}
    >
      {threadCopy(value, subject.kind).label}
    </button>
  );

  return (
    <CommentSubjectKindContext.Provider value={subject.kind}>
      <section
        aria-label={
          subject.kind === 'violation'
            ? 'Violation comments'
            : subject.kind === 'reservation'
              ? 'Reservation comments'
              : 'Work order comments'
        }
      >
        {/* Linear's activity header: the title, and the audience switch on
          the right. Gated on role, not on whether internal comments exist:
          a manager with no internal notes yet must still be shown the
          choice, or the first one gets typed into whichever composer was
          on screen. A board member sees no switch and is never told the
          other thread is there. */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-[15px] font-semibold text-fg">{title}</h2>
          {canPostInternal && (
            <div
              role="group"
              aria-label="Which thread"
              className="flex items-center gap-0.5 rounded-lg border border-line p-0.5"
            >
              {tabButton('internal')}
              {tabButton('shared')}
            </div>
          )}
        </div>

        <ShowAlert
          alertType="error"
          alertTitle="Comment Error"
          alertMessage={error ?? ''}
          isVisible={error !== null}
          onClose={clearError}
          isInline={true}
        />

        {loading ? (
          // A thread that loads quickly just appears; the spinner is for a
          // slow one (#635).
          showSpinner && (
            <div className="mt-4">
              <LoadingSpinner />
            </div>
          )
        ) : (
          <>
            {/* One line down the left joins everything in time order. */}
            <ol
              aria-label="Thread"
              className="relative space-y-4 before:absolute before:bottom-3 before:left-[11px] before:top-3 before:w-px before:bg-line"
            >
              {timeline.map((item) =>
                item.kind === 'event' ? (
                  <ActivityEventRow
                    key={`event-${item.event.id}`}
                    event={item.event}
                    actorName={(id) =>
                      actorName({ author_id: id, author_realm: 'user' })
                    }
                  />
                ) : (
                  <li
                    key={item.node.comment.id}
                    className="relative flex items-start gap-3"
                  >
                    <span className="relative z-10 mt-2 rounded-full bg-panel">
                      <Avatar name={authorName(item.node.comment)} />
                    </span>
                    <CommentNodeView
                      node={item.node}
                      currentUserId={currentUserId}
                      canPostInternal={canPostInternal}
                      canModerate={canModerate}
                      canAttach={canAttach}
                      authorName={authorName}
                      replyingToId={replyingToId}
                      editingId={editingId}
                      busy={busy}
                      onStartReply={startReply}
                      onStartEdit={startEdit}
                      onCancel={cancel}
                      onReply={handleReply}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      highlightedId={highlightCommentId}
                    />
                  </li>
                )
              )}
            </ol>

            {tree.length === 0 && (
              <p className="mt-4 pl-9 text-[13px] text-fg-muted">
                {copy.emptyText}
              </p>
            )}

            {canComment && (
              <div className="mt-4 pl-9">
                {/* Keyed on the tab so a thread switch unmounts it and the
                  draft goes with it. */}
                <CommentComposer
                  key={tab}
                  visibility={tab}
                  busy={busy}
                  allowAttachments={canAttach}
                  onSubmit={post(tab)}
                />
              </div>
            )}
          </>
        )}
      </section>
    </CommentSubjectKindContext.Provider>
  );
}

export default CommentThread;
